// src/components/engineers/EngineerDetailsSidebar.jsx
import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Chip, 
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";

const statusStyles = {
  Active: { bgcolor: "#8CD47E", color: "#ffffff" },
  APPROVED: { bgcolor: "#8CD47E", color: "#ffffff" },
  Pending: { bgcolor: "#F8D66D", color: "#ffffff" },
  PENDING: { bgcolor: "#F8D66D", color: "#ffffff" },
  Rejected: { bgcolor: "#FF6961", color: "#ffffff" },
  REJECTED: { bgcolor: "#FF6961", color: "#ffffff" },
  UNASSIGNED: { bgcolor: "#F8D66D", color: "#fff" },
  ASSIGNED: { bgcolor: "#FFB54C", color: "#fff" },
  VISITED: { bgcolor: "#B3EBF2", color: "#fff" },
  SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
  FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
};

// Get frontend status display
const getFrontendStatus = (backendStatus) => {
  switch (backendStatus) {
    case 'APPROVED':
      return 'Active';
    case 'PENDING':
      return 'Pending';
    case 'REJECTED':
      return 'Rejected';
    default:
      return backendStatus || 'Pending';
  }
};

// Function to get project counts
const getProjectCounts = (engineer) => {
  if (!engineer) {
    return {
      completed: 0,
      assigned: 0,
      display: "0/0",
      tooltip: "Completed: 0\nAssigned: 0"
    };
  }

  let completed = 0;
  let assigned = 0;

  if (engineer.projectsRecords) {
    completed = engineer.projectsRecords.projectCompleted || 0;
    assigned = engineer.projectsRecords.projectAssigned || 0;
  }
  
  return {
    completed,
    assigned,
    display: `${completed}/${assigned}`,
    tooltip: `Completed: ${completed}\nAssigned: ${assigned}`
  };
};

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "N/A";
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')}


const EngineerDetailsSidebar = ({ open, onClose, engineer }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && engineer?._id) {
      fetchEngineerProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, engineer?._id]);

  const fetchEngineerProjects = async () => {
    if (!engineer?._id || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch projects assigned to this engineer
      const response = await axios.get(`${url}/api/engineer/assigned-projects/${engineer._id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          limit: 100 // Fetch all projects for this engineer
        }
      });

      const assignedProjects = response.data?.assignedProjects || [];
      
      // Transform project data
      const transformedProjects = assignedProjects.map(item => {
        // Format Request Date
        let reqDateDisplay = "N/A";
        if (item.preferredSiteVisitDate?.fromDate) {
          const fromDate = new Date(item.preferredSiteVisitDate.fromDate);
          const toDate = item.preferredSiteVisitDate.toDate
            ? new Date(item.preferredSiteVisitDate.toDate)
            : null;

          if (!isNaN(fromDate)) {
            const fromFormatted = formatDate(fromDate);
            if (toDate && !isNaN(toDate)) {
              const toFormatted = formatDate(toDate);
              reqDateDisplay =
                fromDate.toDateString() === toDate.toDateString()
                  ? fromFormatted
                  : `${fromFormatted} - ${toFormatted}`;
            } else {
              reqDateDisplay = fromFormatted;
            }
          }
        } else if (item.requestedDate) {
          reqDateDisplay = formatDate(item.requestedDate);
        }

        // Format VISIT date - FIXED: Use assignedVisitDate to match AllProjects.js
        let visitedDateDisplay = "Not Scheduled";
        if (item.assignedVisitDate) {
          try {
            let date = null;
            if (item.assignedVisitDate.$date) {
              date = new Date(item.assignedVisitDate.$date);
            } else if (item.assignedVisitDate instanceof Date || typeof item.assignedVisitDate === 'string') {
              date = new Date(item.assignedVisitDate);
            }
            
            if (date && !isNaN(date.getTime())) {
              visitedDateDisplay = formatDate(date);
            }
          } catch (error) {
            console.error("Error formatting visited date:", error);
          }
        }

        // Get client details
        let clientName = "N/A";
        let clientContact = "N/A";
        
        if (item.clientID) {
          if (typeof item.clientID === 'object') {
            clientName = item.clientID.name || item.clientID.companyName || "N/A";
            clientContact = item.clientID.phone || "N/A";
          }
        }

        // Get project report number
        const projectReportNo = item.projectReportNo || "—";

        return {
          id: item._id || item.id,
          projectReportNo: projectReportNo,
          projectName: item.nameOfProject || "N/A",
          clientName: clientName,
          clientContact: clientContact,
          siteCheck: item.templateTitle || (item.templateID?.title || "N/A"),
          reqDate: reqDateDisplay,
          assignedVisitDate: visitedDateDisplay,
          status: item.status || "UNKNOWN",
          originalStatus: item.status || "UNKNOWN",
          _id: item._id,
        };
      });

      setProjects(transformedProjects);
    } catch (err) {
      console.error("Error fetching engineer projects:", err);
      setError(err.response?.data?.message || "Failed to load engineer projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  if (!engineer) return null;

  // Get project info
  const projectInfo = getProjectCounts(engineer);
  
  // Get formatted status
  const displayStatus = getFrontendStatus(engineer.status);

  return (
    <>
      {/* Overlay */}
      {open && (
        <Box
          onClick={onClose}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 1299,
          }}
        />
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: 900, // Increased width to accommodate table
          height: "100vh",
          position: "fixed",
          top: 0,
          right: open ? 0 : "-100%",
          bgcolor: "#fff",
          zIndex: 1300,
          transition: "right 0.3s ease",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
          p: 3,
          overflowY: "auto", // Make sidebar scrollable
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 , alignItems: "center"}}>
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Engineer Details
          </Typography>
          <IconButton onClick={onClose} sx={{p:0.5}}>
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Engineer Info */}
        <Box>
          {/* TOP ROW: Name + Joined Date */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            {/* Name + Status */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Icon name="profile" color={COLORS.PRIMARY} />
              <Typography sx={TYPOGRAPHY.H3}>
                {engineer.name}
              </Typography>
              <Chip
                label={displayStatus}
                size="small"
                sx={{
                  ...(statusStyles[engineer.status] || statusStyles.Pending),
                  height: 22,
                  ...TYPOGRAPHY.BODY_SMALL,
                }}
              />
            </Box>

            {/* Joined Date */}
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
              Joined on:{" "}
              {new Date(engineer.joinedAt || engineer.createdAt).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')}
            </Typography>
          </Box>

          {/* Email */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, pl: "24px" }}>
            <Icon name="at" size={16} color={COLORS.SECONDARY} />
            <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY }}>
              {engineer.email}
            </Typography>
          </Box>

          {/* Phone + Projects (Same Row) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: "24px"
            }}
          >
            {/* Phone */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon name="phoneDone" size={16} color={COLORS.SECONDARY} />
              <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY }}>
                {engineer.phone}
              </Typography>
            </Box>

            {/* Projects (Right Side) */}
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
              Projects: {projectInfo.display}
            </Typography>
          </Box>
        </Box>

        {/* NEW: Field Inspection Stats Section */}
        {engineer.fieldInspectionStats && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "#F9FAFB", borderRadius: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="list" size={18} color={COLORS.PRIMARY} />
              Field Inspection Analytics
            </Typography>
            
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", color: COLORS.SECONDARY, mb: 0.5 }}>Individual Responses</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip 
                    label={`YES: ${engineer.fieldInspectionStats.yesResponses || 0}`} 
                    sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 600, height: 26 }} 
                  />
                  <Chip 
                    label={`NO: ${engineer.fieldInspectionStats.noResponses || 0}`} 
                    sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 600, height: 26 }} 
                  />
                </Box>
              </Box>
              
              <Box>
                <Typography sx={{ fontSize: "0.75rem", color: COLORS.SECONDARY, mb: 0.5 }}>Contributions</Typography>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 700 }}>
                  {engineer.fieldInspectionStats.questionsAdded || 0} <span style={{ fontWeight: 400, fontSize: '0.8rem', color: COLORS.SECONDARY }}>Questions Added</span>
                </Typography>
              </Box>

              {engineer.fieldInspectionStats.totalCriticalReports > 0 && (
                <Box sx={{ gridColumn: "span 2", mt: 1, p: 1.5, bgcolor: "#FFF5F5", borderRadius: 1.5, border: "1px solid #FED7D7" }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon name="error" size={16} color={COLORS.ERROR} />
                    <Typography sx={{ color: COLORS.ERROR, fontWeight: 700, fontSize: '0.9rem' }}>
                      Involved in {engineer.fieldInspectionStats.totalCriticalReports} Critical {engineer.fieldInspectionStats.totalCriticalReports === 1 ? 'Report' : 'Reports'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`, mt: 2, mb: 3 }} />

        {/* Projects Section Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
            Assigned Projects ({projects.length})
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.SECONDARY }}>
            Showing all projects assigned to this engineer
          </Typography>
        </Box>

        {/* Projects Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : projects.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 3, border: `1px dashed ${COLORS.LIGHT_SECONDARY}`, borderRadius: 1 }}>
            <Typography sx={{ color: COLORS.TEXT }}>
              No projects assigned to this engineer
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            width: '100%', 
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: COLORS.LIGHT_SECONDARY,
              borderRadius: '4px',
            },
          }}>
            <Paper elevation={0} sx={{ backgroundColor: "transparent", minWidth: '800px' }}>
              <TableContainer>
                <Table
                  sx={{
                    backgroundColor: "transparent",
                    "& thead th": { 
                      backgroundColor: "transparent",
                      padding: "8px 6px",
                    },
                    "& tbody td": { 
                      backgroundColor: "transparent", 
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      padding: "8px 6px",
                      verticalAlign: 'top',
                    },
                    tableLayout: 'fixed',
                    width: '100%',
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        { key: "Project Name", width: "200px" },
                        { key: "Client Name", width: "150px" },
                        { key: "Site Check", width: "120px" },
                        { key: "Req Date", width: "100px" },
                        { key: "Visit Date", width: "100px" },
                        { key: "Status", width: "100px" },
                      ].map((head) => (
                        <TableCell
                          key={head.key}
                          sx={{
                            ...TYPOGRAPHY.H4,
                            color: COLORS.PRIMARY,
                            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            whiteSpace: "nowrap",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            width: head.width,
                            minWidth: head.width,
                            maxWidth: head.width,
                            padding: "8px 6px",
                          }}
                        >
                          {head.key}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                        {/* Project Name Column */}
                        <TableCell sx={{ 
                          width: '200px', 
                          minWidth: '200px', 
                          maxWidth: '200px',
                          padding: "8px 6px"
                        }}>
                          {/* Report Number Badge - Only show for FINALREPORT status */}
                          {project.status === "FINALREPORT" && project.projectReportNo && project.projectReportNo !== "—" ? (
                            <Box 
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                border: `1.5px solid ${COLORS.PRIMARY}`,
                                backgroundColor: COLORS.SECONDARY,
                                color: COLORS.BACKGROUND,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                marginBottom: '6px',
                                width: 'fit-content',
                                minWidth: '60px',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                              }}
                            >
                              {project.projectReportNo}
                            </Box>
                          ) : (
                            <Typography 
                              sx={{ 
                                ...TYPOGRAPHY.BODY_SMALL, 
                                color: COLORS.PRIMARY, 
                                fontSize: "1rem",
                                marginBottom: '6px',
                                fontWeight: 600
                              }}
                            >
                              —
                            </Typography>
                          )}
                          <Typography 
                            sx={{ 
                              ...TYPOGRAPHY.BODY_LARGE, 
                              color: COLORS.TEXT, 
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {project.projectName}
                          </Typography>
                        </TableCell>

                        {/* Client Name Column */}
                        <TableCell sx={{ 
                          width: '150px', 
                          minWidth: '150px', 
                          maxWidth: '150px',
                          padding: "8px 6px"
                        }}>
                          <Typography 
                            sx={{ 
                              ...TYPOGRAPHY.BODY_LARGE, 
                              color: COLORS.TEXT, 
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {project.clientName}
                          </Typography>
                          {project.clientContact && (
                            <Typography 
                              sx={{ 
                                ...TYPOGRAPHY.BODY_SMALL, 
                                color: COLORS.TEXT, 
                                fontSize: "0.7rem",
                                wordBreak: 'break-word',
                                lineHeight: 1.2
                              }}
                            >
                              {project.clientContact}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Site Check Column */}
                        <TableCell 
                          sx={{ 
                            width: '120px', 
                            minWidth: '120px', 
                            maxWidth: '120px',
                            padding: "8px 6px",
                            color: COLORS.TEXT, 
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.siteCheck}
                        </TableCell>
                        
                        {/* Request Date Column */}
                        <TableCell 
                          sx={{ 
                            width: '100px', 
                            minWidth: '100px', 
                            maxWidth: '100px',
                            padding: "8px 6px",
                            color: COLORS.TEXT, 
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.reqDate}
                        </TableCell>
                        
                        {/* Visit Date Column */}
                        <TableCell 
                          sx={{ 
                            width: '100px', 
                            minWidth: '100px', 
                            maxWidth: '100px',
                            padding: "8px 6px",
                            color: COLORS.TEXT, 
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.assignedVisitDate}
                        </TableCell>
                        
                        {/* Status Column */}
                        <TableCell sx={{ 
                          width: '100px', 
                          minWidth: '100px', 
                          maxWidth: '100px',
                          padding: "8px 6px"
                        }}>
                          <Chip
                            label={project.status}
                            size="small"
                            sx={{ 
                              ...(statusStyles[project.status] || {}), 
                              height: 20, 
                              ...TYPOGRAPHY.BODY_SMALL,
                              fontSize: "0.65rem",
                              textTransform: 'capitalize',
                              '& .MuiChip-label': {
                                padding: '0 6px',
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}
      </Box>
    </>
  );
};

export default EngineerDetailsSidebar;